import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { QrCode, Link2, BarChart3, ArrowRight, Layers, GitCompare, Boxes } from 'lucide-react';
export const Route = createFileRoute('/_layout/learn/')({
    component: LearnPage,
    head: () => ({
        meta: [
            { title: 'Learn Offline Marketing Analytics — Guides by Adora' },
            {
                name: 'description',
                content: 'Guides and explanations on QR scan tracking, Smart Links, offline marketing analytics, and how to measure physical campaigns without guesswork.',
            },
            { property: 'og:title', content: 'Learn Offline Marketing Analytics — Guides by Adora' },
            {
                property: 'og:description',
                content: 'Guides and explanations on QR scan tracking, Smart Links, offline marketing analytics, and how to measure physical campaigns without guesswork.',
            },
        ],
    }),
});
function LearnPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* HERO */}
            <section className="max-w-2xl">
                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Learn offline marketing analytics</h1>
                <p className="mt-3 text-muted-foreground">
                    Guides and explanations to help you understand how QR codes, smart links, and offline campaigns can
                    be measured — without guesswork.
                </p>
            </section>

            <Separator className="my-10"/>

            {/* CORE CONCEPTS */}
            <section>
                <h2 className="text-xl font-semibold mb-4">Core concepts</h2>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <LearnCard icon={<BarChart3 className="h-5 w-5"/>} title="Offline marketing analytics" description="How physical materials become measurable campaigns." to="/learn/offline-marketing-analytics"/>

                    <LearnCard icon={<QrCode className="h-5 w-5"/>} title="QR scan tracking" description="What happens when someone scans a QR code — and what you can measure." to="/learn/qr-scan-tracking"/>

                    <LearnCard icon={<Link2 className="h-5 w-5"/>} title="Smart Links" description="Track different contexts without changing destinations." to="/learn/smart-links"/>

                    <LearnCard icon={<Boxes className="h-5 w-5"/>} title="One-link engine" description="One printed URL, four modes. Print once, decide later, measure everything." to="/learn/one-link-engine"/>
                </div>
            </section>

            <Separator className="my-10"/>

            {/* COMPARISONS & CONTEXT */}
            <section>
                <h2 className="text-xl font-semibold mb-4">Comparisons & context</h2>

                <div className="grid gap-4 sm:grid-cols-2">
                    <LearnCard icon={<Layers className="h-5 w-5"/>} title="Smart Links vs link-in-bio" description="Why smart links are not just another bio page." to="/learn/smart-links-vs-link-in-bio"/>

                    <LearnCard icon={<GitCompare className="h-5 w-5"/>} title="Compare Adora with other tools" description="Understand when Adora is the right choice." to="/compare"/>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* CTA */}
            <Card>
                <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Ready to apply what you’ve learned?</h3>
                        <p className="text-sm text-muted-foreground">
                            Create a campaign and start measuring your offline materials.
                        </p>
                    </div>

                    <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 text-primary font-medium hover:underline">
                        Create free account
                        <ArrowRight className="h-4 w-4"/>
                    </Link>
                </CardContent>
            </Card>
        </main>);
}
/* ---------- */
function LearnCard({ icon, title, description, to, }) {
    return (<Card className="h-full transition hover:shadow-md hover:bg-muted/30">
            <CardHeader className="space-y-2">
                <CardTitle className="flex items-center gap-2 text-base">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
                <p>{description}</p>
                <Link to={to} className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                    Read guide
                    <ArrowRight className="h-3.5 w-3.5"/>
                </Link>
            </CardContent>
        </Card>);
}
